/**
 * The account record page's body — no prototype exists for this (accounts.html
 * is the list only), so the layout follows the lead detail page's own
 * structure instead: a stats/hierarchy sidebar, a tabbed centre column, real
 * data everywhere the backend actually has it, and an honest "not built yet"
 * card where a sub-resource genuinely isn't (merge, bulk-assign, duplicates).
 * Opportunities (`GET /opportunities?account_id=`) and Documents (§11's
 * presigned pair via `modules/files`) were both "not built" placeholders
 * until this pass wired them to the real, already-implemented endpoints.
 */
import { useState } from 'react';
import { Card, EmptyState, Avatar } from '../../components/common/index.jsx';
import { SkeletonText } from '../../components/common/Loading.jsx';
import { ActivityTab as SharedActivityTab } from '../activities/ActivityTimeline.jsx';
import { money, moneyShort, number, date, relativeTime } from '../../utils/formatters.js';
import { useAccountHierarchy, useAccountContacts } from './useAccounts.js';
import { STAGE_PILL } from './accountShape.js';
import { useOpportunities } from '../opportunities/useOpportunities.js';
import { toRow as toOpportunityRow, forecastTone } from '../opportunities/opportunityShape.js';
import { DocumentsPanel } from '../documents/DocumentsPanel.jsx';

/* -------------------------------------------------------------------------- */
/* Sidebar                                                                    */
/* -------------------------------------------------------------------------- */

export function StatsCard({ account }) {
  const s = account.stats ?? {};
  return (
    <Card title="Pipeline" className="fade-up mb-3">
      <div className="row g-2 t-sm">
        <div className="col-6"><div className="ink-4 t-2xs">Open opportunities</div><div className="fw-750 t-sm">{number(s.open_opportunities ?? 0)}</div></div>
        <div className="col-6"><div className="ink-4 t-2xs">Open value</div><div className="fw-750 t-sm tabular">{money(s.open_value ?? 0)}</div></div>
        <div className="col-6"><div className="ink-4 t-2xs">Won, last 12mo</div><div className="fw-750 t-sm tabular">{money(s.won_value_ltm ?? 0)}</div></div>
        <div className="col-6"><div className="ink-4 t-2xs">Contacts</div><div className="fw-750 t-sm">{number(s.contacts ?? 0)}</div></div>
      </div>
      <hr className="soft" />
      <div className="t-xs ink-3">
        Last activity {s.last_activity_at ? relativeTime(s.last_activity_at) : 'never'}.
      </div>
      <div className="t-2xs ink-4 mt-1">
        <i className="bi bi-info-circle me-1" aria-hidden="true" />
        Computed per request, never stored — a cached figure here goes stale the moment a deal moves.
      </div>
    </Card>
  );
}

export function QuickFactsCard({ account }) {
  return (
    <Card title="Account details" className="fade-up mb-3">
      <dl className="detail-grid mb-0">
        <div className="detail-row"><dt>Owner</dt><dd>{account.owner?.full_name ?? 'Unowned'}</dd></div>
        <div className="detail-row"><dt>Territory</dt><dd>{account.territory?.name ?? '—'}</dd></div>
        <div className="detail-row"><dt>Price list</dt><dd>{account.priceList?.name ?? '—'}</dd></div>
        <div className="detail-row"><dt>Payment terms</dt><dd>{account.paymentTerms ?? '—'}</dd></div>
        <div className="detail-row"><dt>Currency</dt><dd>{account.currency ?? '—'}</dd></div>
        <div className="detail-row"><dt>Credit limit</dt><dd>{account.creditLimit ? money(account.creditLimit) : '—'}</dd></div>
        <div className="detail-row"><dt>Tax number</dt><dd>{account.taxNumber ?? '—'}</dd></div>
        <div className="detail-row"><dt>Registration no.</dt><dd>{account.registrationNumber ?? '—'}</dd></div>
      </dl>
    </Card>
  );
}

function AddressBlock({ address, sameAsBilling }) {
  if (sameAsBilling) return <div className="t-sm ink-3">Same as billing address.</div>;
  if (!address || (!address.line1 && !address.city && !address.country)) {
    return <div className="t-sm ink-3">Not on file.</div>;
  }
  return (
    <div className="t-sm">
      {address.line1 && <div>{address.line1}</div>}
      {address.line2 && <div>{address.line2}</div>}
      <div>{[address.city, address.state, address.postal_code].filter(Boolean).join(', ')}</div>
      <div>{address.country}</div>
    </div>
  );
}

export function AddressCard({ account }) {
  return (
    <Card title="Billing & shipping" className="fade-up mb-3">
      <div className="eyebrow mb-1">Billing address</div>
      <AddressBlock address={account.billingAddress} />
      <hr className="soft" />
      <div className="eyebrow mb-1">Shipping address</div>
      <AddressBlock address={account.shippingAddress} sameAsBilling={account.shippingSameAsBilling} />
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Hierarchy tab                                                             */
/* -------------------------------------------------------------------------- */

export function HierarchyTab({ account, onOpen }) {
  const { data, isLoading, error } = useAccountHierarchy(account.id, { direction: 'both', depth: 5, rollup: true });

  if (isLoading) return <SkeletonText lines={4} />;
  if (error) return <div className="t-sm ink-3">Could not load the hierarchy.</div>;

  const nodes = data?.nodes ?? [];
  if (nodes.length <= 1) {
    return (
      <EmptyState
        icon="bi-diagram-3" title="No hierarchy"
        message="This account has no parent and no subsidiaries. Set a parent account from Edit to place it in a group."
      />
    );
  }

  return (
    <div>
      {nodes.map((n) => (
        <div
          key={n.id}
          className="d-flex align-items-center gap-2 py-2"
          style={{ paddingLeft: n.depth * 24, borderBottom: '1px solid var(--line-2)', cursor: n.id === account.id ? 'default' : 'pointer' }}
          onClick={() => n.id !== account.id && onOpen(n.id)}
        >
          <i className={`bi ${n.id === account.id ? 'bi-building-fill' : 'bi-building'}`} style={{ color: n.id === account.id ? 'var(--brand-600)' : 'var(--ink-4)' }} aria-hidden="true" />
          <div className="min-w-0 flex-grow-1">
            <div className={`t-sm ${n.id === account.id ? 'fw-750' : 'fw-600'}`}>{n.name}</div>
            <div className="t-2xs ink-4">{n.account_no} · {n.lifecycle_stage}</div>
          </div>
          <span className={`pill ${STAGE_PILL[n.lifecycle_stage] ?? 'pill-gray'}`}>{n.lifecycle_stage}</span>
          {n.rollup && (
            <span className="t-2xs ink-3 no-wrap" style={{ minWidth: 140, textAlign: 'right' }}>
              {n.rollup.account_count} account{n.rollup.account_count === 1 ? '' : 's'} · {moneyShort(n.rollup.open_value)} open
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Contacts tab                                                               */
/* -------------------------------------------------------------------------- */

export function ContactsTab({ account }) {
  const { data, isLoading, error } = useAccountContacts(account.id, { limit: 50 });
  const contacts = data?.data ?? [];

  if (isLoading) return <SkeletonText lines={4} />;
  if (error) return <div className="t-sm ink-3">Could not load contacts.</div>;
  if (!contacts.length) {
    return <EmptyState icon="bi-person-lines-fill" title="No contacts yet" message="People at this company appear here once they're added to Contacts." />;
  }

  return (
    <div>
      {contacts.map((c) => (
        <div key={c.id} className="d-flex align-items-center gap-2 py-2" style={{ borderBottom: '1px solid var(--line-2)' }}>
          <Avatar name={c.full_name} size={32} />
          <div className="min-w-0 flex-grow-1">
            <div className="t-sm fw-650 d-flex align-items-center gap-2">
              {c.full_name}
              {c.is_primary && <span className="pill pill-brand">Primary</span>}
            </div>
            <div className="t-2xs ink-4">{c.job_title ?? '—'} {c.email ? `· ${c.email}` : ''}</div>
          </div>
          {c.buying_role && <span className="pill pill-gray no-wrap">{c.buying_role}</span>}
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Activities tab — the shared timeline, scoped to this account               */
/* -------------------------------------------------------------------------- */

export function ActivitiesTab({ account }) {
  return <SharedActivityTab linkField="account_id" recordId={account.id} />;
}

/* -------------------------------------------------------------------------- */
/* Opportunities tab — GET /opportunities?account_id=, real and implemented   */
/* despite the header comment above (that survey predates this module) */
/* -------------------------------------------------------------------------- */

export function OpportunitiesTab({ account }) {
  const { data, isLoading, error } = useOpportunities({ account_id: account.id, limit: 50, sort: '-updated_at' });
  const rows = (data?.data ?? []).map(toOpportunityRow);

  if (isLoading) return <SkeletonText lines={4} />;
  if (error) return <div className="t-sm ink-3">Could not load opportunities.</div>;
  if (!rows.length) {
    return (
      <EmptyState
        icon="bi-briefcase" title="No opportunities yet"
        message="Deals for this account appear here once they're created in Opportunities."
      />
    );
  }

  return (
    <div>
      {rows.map((o) => (
        <div key={o.id} className="d-flex align-items-center gap-2 py-2" style={{ borderBottom: '1px solid var(--line-2)' }}>
          <div className="min-w-0 flex-grow-1">
            <div className="t-sm fw-650 text-truncate">{o.name}</div>
            <div className="t-2xs ink-4">
              {o.owner?.full_name ?? 'Unowned'} · {o.expectedCloseDate ? date(o.expectedCloseDate) : 'No close date'}
            </div>
          </div>
          <span className={`pill pill-${forecastTone(o.stage?.forecast_category)} no-wrap`}>{o.stage?.name ?? '—'}</span>
          <span className="t-sm fw-750 tabular no-wrap" style={{ minWidth: 100, textAlign: 'right' }}>
            {money(o.amount, o.currency)}
          </span>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Documents tab — §11's presigned pair, scoped to this account               */
/* -------------------------------------------------------------------------- */

export function DocumentsTab({ account }) {
  return <DocumentsPanel entityKey="account" recordId={account.id} />;
}

/* -------------------------------------------------------------------------- */
/* Not-built tabs                                                             */
/* -------------------------------------------------------------------------- */

export function NotBuiltTab({ title, message, endpoint, section }) {
  return (
    <EmptyState icon="bi-cone-striped" title={title} message={message}>
      <div className="t-2xs ink-4 mt-2">Endpoint: <code>{endpoint}</code> — {section}</div>
    </EmptyState>
  );
}
